import { ArrowRight, Building2, TrendingUp, Clock, Shield, CheckCircle, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { BRAND } from "@/lib/brand-config";
// Use static path for LCP image to enable preloading in index.html
const heroBg = "/images/hero-dual-path-bg.jpg";

const DualPathHero = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-primary">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={heroBg}
          alt="Commercial real estate investment opportunities"
          className="w-full h-full object-cover"
          width={1920}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/85 to-primary/80" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10 py-16 md:py-20">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center max-w-7xl mx-auto">
          {/* Left: Hero Content */}
          <div className="text-white space-y-8">
            {/* Urgency Badge */}
            <div className="inline-flex items-center gap-2 bg-warning/20 backdrop-blur-sm border border-warning/40 rounded-full px-4 py-2 urgency-badge">
              <Zap className="w-4 h-4 text-warning" />
              <span className="text-warning font-semibold text-sm">48-Hour Funding Available</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Commercial Property Loans in 48 Hours.
              <span className="block text-accent mt-2">No Bank Required.</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-white/80 max-w-xl">
              Access $250K-$50M for investors and developers who can't wait for traditional banks.
            </p>

            {/* Dual CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 pt-4">
              <Link to="/borrow-commercial" className="flex-1">
                <Button 
                  size="lg" 
                  className="w-full text-lg py-7 bg-accent hover:bg-accent/90 text-accent-foreground gap-2 font-semibold shadow-lg"
                >
                  <Building2 className="w-5 h-5" />
                  I Need a Commercial Loan
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/invest-private" className="flex-1">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full text-lg py-7 bg-white/10 border-white/30 text-white hover:bg-white/20 gap-2 font-semibold"
                >
                  <TrendingUp className="w-5 h-5" />
                  I Want to Invest
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-4 pt-4 text-sm text-white/70">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>Licensed Mortgage Agent</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-success" />
                <span>FSRA Regulated</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-success" />
                <span>200+ Clients Served</span>
              </div>
            </div>
          </div>

          {/* Right: Live Stats Dashboard */}
          <div className="hidden lg:block">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
                <span className="text-white font-semibold">Live Activity</span>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-1">
                  <p className="text-white/60 text-sm">Loans Funded This Month</p>
                  <p className="text-3xl font-bold text-white">{BRAND.stats.fundedLoans}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-white/60 text-sm">Average Approval Time</p>
                  <p className="text-3xl font-bold text-accent">{BRAND.stats.avgApprovalTime}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-white/60 text-sm">Clients Served</p>
                  <p className="text-3xl font-bold text-white">{BRAND.stats.clientsSatisfied}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-white/60 text-sm">Default Rate</p>
                  <p className="text-3xl font-bold text-success">{BRAND.stats.defaultRate}</p>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="border-t border-white/20 pt-6 space-y-3">
                <p className="text-white/60 text-sm">Recent Approvals</p>
                <div className="space-y-2">
                  {[
                    { type: "Multi-Family", amount: "$2.3M", time: "2 hours ago" },
                    { type: "Commercial", amount: "$890K", time: "5 hours ago" },
                    { type: "Mixed-Use", amount: "$1.5M", time: "Yesterday" },
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <span className="text-white">{item.type}</span>
                      <span className="text-accent font-semibold">{item.amount}</span>
                      <span className="text-white/50">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent" />
    </section>
  );
};

export default DualPathHero;
